import {Component, EventEmitter, HostListener, inject, Input, OnInit, Output} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromApp from "../../../../app.reducer";
import {getPlaceOrderState} from "../../../../app.reducer";
import {Subscription} from "rxjs";
import {CartRequestDetails} from "../../../../@domain/cart-request-details";
import {CartDeleteDialogComponent} from "../../place-order/order-cart/cart-delete-dialog/cart-delete-dialog.component";
import {CLEAR_CART, DELETE_FOOD_FORM_CART} from "../../store/place-order/place-order.action";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {FoodItemQuantity} from "../../../../@domain/food-item-quantity";
import {FoodItem} from "../../../../@domain/food-item";
import {ApiBaseService, StorageService} from '../../../../../../projects/vendors/src/public-api';
import {PosCheckoutDialogComponent} from "../pos-checkout-dialog/pos-checkout-dialog.component";
import {BaseComponent} from "../../../../@application/component/base.component";
import {FoodItemPipe} from "../../../../../../projects/vendors/src/lib/pipe/food-item.pipe";
import {KotPrintDialogComponent} from "../../place-order/order-cart/kot-print-dialog/kot-print-dialog.component";

@Component({
    selector: 'app-pos-cart',
    templateUrl: './pos-cart.component.html',
    styleUrl: './pos-cart.component.scss',
    standalone: false
})
export class PosCartComponent extends BaseComponent implements OnInit {

  cartFoods: CartRequestDetails[] = [];
  isOrderPrinted = false
  @Output() orderPrinted: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() sectionId!: string
  dialog = inject(MatDialog)
  foodItemQuantity = [] as FoodItemQuantity[]
  foodItems = [] as FoodItem[];
  apiBaseService = inject(ApiBaseService)
  storageService = inject(StorageService)
  private store = inject(Store<fromApp.AppState>)
  private foodNamePipe = inject(FoodItemPipe)
  // Set only when this cart is the one rendered inside PosCartDialogComponent (tablet/mobile).
  private hostDialogRef = inject(MatDialogRef, {optional: true})

  // Keyboard navigation properties
  selectedCartIndex = -1;
  lastEnterTime = 0;

  ngOnInit() {
    this.subscribe()
  }

  private subscribe() {
    this.subscription$.add(
      this.store.select(getPlaceOrderState)
        .subscribe({
          next: value => {
            this.cartFoods = value.cartFoods
            this.isOrderPrinted = value.cartFoods.length > 0
          }
        })
    )
    this.subscription$.add(
      this.store.select(fromApp.getMenuState)
        .subscribe({
          next: value => {
            this.foodItems = value.menuRelatedData.foodItem
            this.foodItemQuantity = value.menuRelatedData.quantity
          }
        })
    )
  }

  isNameContainsSpecialCharacter(name: string): boolean {
    const foodName = this.foodNamePipe.transform(name, this.foodItems);
    const bracketsRegex = /[()]/;
    return bracketsRegex.test(foodName);
  }

  getTotalQuantity(): number {
    return this.cartFoods?.reduce((sum, item) => sum + item.quantity, 0);
  }

  getSubtotal(): number {
    return this.cartFoods.reduce((acc, item) => acc + item.price * item.quantity, 0);
  }

  onDeleteFood(foodItem: CartRequestDetails): void {
    if (foodItem.id !== undefined) {
      const dialogRef = this.dialog.open(CartDeleteDialogComponent, {
        width: '25%',
        data: foodItem.name
      })
    } else {
      this.store.dispatch(DELETE_FOOD_FORM_CART({payload: foodItem}))
    }
  }

  onCloseOrder() {
    // 70vw is 273px on a phone - too narrow for the payment grid and numpad.
    const isPhone = window.matchMedia('(max-width: 767.98px)').matches;
    const dialogRef = this.dialog.open(PosCheckoutDialogComponent, {
      width: isPhone ? '100vw' : '70vw',
      maxWidth: isPhone ? '100vw' : '70vw',
      height: isPhone ? '100vh' : '90vh',
      maxHeight: isPhone ? '100vh' : '90vh',
      panelClass: 'checkout-dialog-panel',
      data: {
        cartFoods: this.cartFoods,
        foods: this.foodItems,
        sectionId: this.sectionId
      }
    })
    dialogRef.afterClosed().subscribe({
      next: value => {
        // Only a saved order closes with a value; ESC/backdrop/X close with undefined
        // and must leave the cart alone.
        if (value) {
          this.orderPrinted.emit(true)
          this.store.dispatch(CLEAR_CART())
        }
      }
    })
  }

  onPrintKot() {
    this.dialog.open(KotPrintDialogComponent, {
      width: '70vw',
      maxWidth: '70vw',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'kot-print-panel',
      data: {
        orderId: 'POS',
        cartFoods: this.cartFoods,
        foodItems: this.foodItems,
        orderDetails: {
          orderNumber: 'POS',
          sectionName: 'N/A',
          sectionId: 'POS',
          tables: [],
          orderType: 100
        }
      }
    })
  }

  onDeleteClick(event: Event, foodItem: CartRequestDetails): void {
    event.stopPropagation();
    this.onDeleteFood(foodItem);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Skip if a dialog is open on top of this cart. The cart itself may be hosted in a
    // dialog on tablet/mobile, so ignore that one - otherwise it blocks its own shortcuts.
    if (this.dialog.openDialogs.length > (this.hostDialogRef ? 1 : 0)) {
      return;
    }

    // Skip if user is typing in an input field
    const activeElement = document.activeElement;
    const isInputFocused = activeElement instanceof HTMLInputElement ||
                           activeElement instanceof HTMLTextAreaElement;
    if (isInputFocused) return;

    // Ctrl+P - Print KOT
    if (event.ctrlKey && event.key === 'p') {
      event.preventDefault();
      this.onPrintKot();
      return;
    }

    // Double Enter - Checkout
    if (event.key === 'Enter') {
      const now = Date.now();
      if (now - this.lastEnterTime < 300) {
        event.preventDefault();
        if (this.cartFoods.length > 0) {
          this.onCloseOrder();
        }
        this.lastEnterTime = 0;
        return;
      }
      this.lastEnterTime = now;
      return;
    }

    // Arrow Up - Navigate cart items up
    if (event.key === 'ArrowUp' && event.shiftKey) {
      event.preventDefault();
      this.navigateCart('up');
      return;
    }

    // Arrow Down - Navigate cart items down
    if (event.key === 'ArrowDown' && event.shiftKey) {
      event.preventDefault();
      this.navigateCart('down');
      return;
    }

    // Delete key - Delete selected cart item
    if (event.key === 'Delete' && this.selectedCartIndex >= 0) {
      event.preventDefault();
      const selectedItem = this.cartFoods[this.selectedCartIndex];
      if (selectedItem) {
        this.onDeleteFood(selectedItem);
        this.selectedCartIndex = -1;
      }
      return;
    }

    // Escape - Clear cart selection
    if (event.key === 'Escape') {
      this.selectedCartIndex = -1;
      return;
    }
  }

  private navigateCart(direction: 'up' | 'down'): void {
    if (!this.cartFoods || this.cartFoods.length === 0) return;

    if (direction === 'up') {
      this.selectedCartIndex = this.selectedCartIndex > 0
        ? this.selectedCartIndex - 1
        : this.cartFoods.length - 1;
    } else {
      this.selectedCartIndex = this.selectedCartIndex < this.cartFoods.length - 1
        ? this.selectedCartIndex + 1
        : 0;
    }

    // Scroll selected item into view
    setTimeout(() => {
      const cartElements = document.querySelectorAll('.swipe-container');
      const element = cartElements[this.selectedCartIndex] as HTMLElement;
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }
}
